
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { Box, Card, Chip, makeStyles, Paper, Typography, Dialog, DialogTitle, DialogContent, IconButton, Button, useTheme, } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import Page from "../../components/Page";
import { StoreContext } from "../../store";
import useFillStore from "../../hooks/useFillStore";
import { withAuthentication } from "../../components/Authentication";
import ChatWhatsAppLike from "../../components/whatsapp/ChatWhatsAppLike";
import WhatsAppScan from "../../components/whatsapp/WhatsAppScan";
import { CircularProgress } from "@material-ui/core";
import { PermissionGate, PermissionGuard } from "../../utils/PermissionProvider";
import { getWaStatus } from "../../utils/whatsapp";
import { FeatureGuard } from "../../components/subscriptions";
const useStyles = makeStyles(() => ({
    card: { padding: 16 },
    hint: { marginTop: 8 }
}));
async function fetchData(store) {
    const start = Date.now();
    const results = await Promise.all([store.property.fetch(), store.room.fetch()]);
    const elapsed = Date.now() - start;
    const minDuration = 1000; // 1 detik
    if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
    }
    return results;
}
const WhatsAppIndex = observer(function WhatsAppIndex() {
    const store = useContext(StoreContext);
    const classes = useStyles();
    const theme = useTheme();
    const [fetching] = useFillStore(fetchData, []);
    const properties = store.property.items || [];
    const [scanOpen, setScanOpen] = useState(false);
    const params = new URLSearchParams(window.location.search);
    const chatIdNotification = params.get("chatId");
    const propertyIdNotification = params.get("propertyId");
    // ===== Property Selection =====
    const [selectedPropertyId, setSelectedPropertyId] = useState(() => {
        // Handle notification property first, fallback to first property
        if (propertyIdNotification && properties.find((p) => p._id === propertyIdNotification)) {
            return propertyIdNotification;
        }
        return properties?.[0]?._id;
    });
    const selectedProperty = useMemo(() => properties.find((p) => p._id === selectedPropertyId) || properties[0], [properties, selectedPropertyId]);
    // ===== Session Status =====
    const session = selectedProperty?._id ? store.whatsapp.getSession(selectedProperty._id) : {};
    const qrCode = session.qrCode || null;
    const statusSession = session.status;
    const status = getWaStatus(statusSession);
    // ===== Fallback Account Logic =====
    // Use the Whatsapp store as the single source of truth for usingFallback.
    const autoUsingFallback = status.code !== "connected";
    const usingFallback = store.whatsapp.usingFallback;
    // Ensure store defaults to autoUsingFallback when property changes or status changes
    useEffect(() => {
        try {
            if (typeof store?.whatsapp !== "undefined") {
                if (store.whatsapp.usingFallback !== autoUsingFallback) {
                    store.whatsapp.usingFallback = autoUsingFallback;
                }
            }
        }
        catch (err) {
            // ignore
        }
    }, [autoUsingFallback, selectedProperty?._id, store]);
    const setUsingFallback = useCallback((value) => {
        try {
            if (store && store.whatsapp) {
                if (typeof store.whatsapp.setUsingFallback === "function") {
                    store.whatsapp.setUsingFallback(value);
                }
                else {
                    store.whatsapp.usingFallback = Boolean(value);
                }
            }
        }
        catch (err) {
            // ignore
        }
    }, [store]);
    // ===== Event Handlers =====
    const handleOpenScan = useCallback(async () => {
        try {
            if (!selectedProperty?._id || !selectedProperty?.phone) {
                console.warn("Property belum lengkap");
                store.pushToastMessage?.({
                    message: "Data properti belum lengkap. Pastikan properti memiliki nomor WhatsApp.",
                    severity: "warning"
                });
                return;
            }
            console.log("Starting session for property:", selectedProperty._id);
            await store.whatsapp.startSession(selectedProperty._id);
            setScanOpen(true);
        }
        catch (err) {
            console.error("[handleOpenScan]", err);
            store.pushToastMessage?.({
                message: "Gagal memulai sesi WhatsApp. Silakan coba lagi.",
                severity: "error"
            });
        }
    }, [selectedProperty, store]);
    const handleCloseScan = useCallback(() => {
        setScanOpen(false);
    }, []);
    const handleAfterSend = useCallback((payload) => {
        // Optional hook after send message
        console.log("Message sent:", payload);
    }, []);
    const handleScanSuccess = useCallback(async () => {
        try {
            if (selectedProperty?._id) {
                // Refresh property and room data after successful connection
                await Promise.all([store.property.fetchOne(selectedProperty._id), store.room.fetch()]);
                // Fetch fresh status
                if (selectedProperty.phone) {
                    await store.whatsapp.fetchStatus?.(selectedProperty._id, selectedProperty.phone);
                }
            }
            setScanOpen(false);
            store.pushToastMessage?.({
                message: "WhatsApp berhasil terhubung!",
                severity: "success"
            });
        }
        catch (err) {
            console.error("[handleScanSuccess]", err);
            setScanOpen(false);
        }
    }, [store, selectedProperty]);
    // ===== Connection Setup Effects =====
    // Setup superkos chat fetching when using fallback
    useEffect(() => {
        if (!usingFallback || !selectedProperty?._id)
            return;
        const setupSuperkos = async () => {
            try {
                await store.whatsapp.fetchChats(selectedProperty._id, true); // isSuperkos = true
            }
            catch (err) {
                store.pushToastMessage?.({
                    message: "Gagal memuat chat Superkos.",
                    severity: "warning"
                });
            }
        };
        setupSuperkos();
    }, [usingFallback, selectedProperty?._id, store]);
    // Setup WhatsApp connection when property changes
    useEffect(() => {
        if (!selectedProperty?._id || !selectedProperty?.phone)
            return;
        const setupWhatsApp = async () => {
            try {
                const propertyId = selectedProperty._id;
                const phoneNumber = selectedProperty.phone;
                // Check if already connected/subscribed
                const connectionState = store.whatsapp.getConnectionState(propertyId);
                const isSubscribed = store.whatsapp.isSubscribed(propertyId);
                // Only setup socket connection if not already connected
                if (!isSubscribed || connectionState !== "connected") {
                    await store.whatsapp.subscribeSocket(propertyId, phoneNumber);
                }
                // Always fetch status and chats (they're cached/deduplicated in store)
                await store.whatsapp.fetchChats(propertyId, false); // Regular chats
            }
            catch (err) {
                // Don't show error toast here as it's called on every property change
                // The connection will be retried automatically
            }
        };
        setupWhatsApp();
    }, [selectedProperty?._id, selectedProperty?.phone, store]);
    // Handle property change from notification
    useEffect(() => {
        if (propertyIdNotification && properties.length > 0) {
            const notificationProperty = properties.find((p) => p._id === propertyIdNotification);
            if (notificationProperty && selectedPropertyId !== propertyIdNotification) {
                console.log("Setting property from notification:", propertyIdNotification);
                setSelectedPropertyId(propertyIdNotification);
            }
        }
    }, [propertyIdNotification, properties, selectedPropertyId]);
    // ===== Loading State =====
    if (fetching) {
        return (<Box position="fixed" top={0} left={0} width="100vw" height="100vh" zIndex={theme.zIndex.appBar} style={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}>
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" flexDirection="column" data-cy="loading">
          <CircularProgress color="primary" size={48}/>
          <Typography variant="subtitle1" style={{ marginTop: 24, fontWeight: 500 }}>
            Memuat data awal WhatsApp, mohon tunggu...
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Menghubungkan ke server dan memuat properti
          </Typography>
        </Box>
      </Box>);
    }
    // ===== No Properties State =====
    if (!properties.length) {
        return (<PermissionGate permission="whatsapp">
        <FeatureGuard featureName="Chat WhatsApp" minPlan="standard">
          <Page maxWidth="xl">
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" textAlign="center">
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Tidak ada properti yang tersedia
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Anda perlu memiliki minimal satu properti untuk menggunakan WhatsApp
              </Typography>
            </Box>
          </Page>
        </FeatureGuard>
      </PermissionGate>);
    }
    // ===== Main Render =====
    return (<PermissionGate permission="whatsapp">
      <FeatureGuard featureName="Chat WhatsApp" minPlan="standard">
        <Page maxWidth="xl">
          <Paper style={{ marginBottom: 20 }}>
            <ChatWhatsAppLike properties={properties} selectedProperty={selectedProperty} onSelectProperty={setSelectedPropertyId} usingFallback={usingFallback} onChangeUsingFallback={setUsingFallback} session={session} store={store} onAfterSend={handleAfterSend} onOpenScan={handleOpenScan} chatIdNotification={chatIdNotification} propertyIdNotification={propertyIdNotification}/>
          </Paper>

          {/* Fallback Account Notice */}
          {/* {usingFallback && selectedProperty && (
        <Card className={classes.card}>
          <Box display="flex" alignItems="center" gap={1}>
            <AlertTriangle size={20} color="#d97706" />
            <Typography variant="body2" color="textSecondary">
              WhatsApp untuk properti <strong>{selectedProperty.name}</strong> sedang{' '}
              <span style={{ color: '#dc2626', fontWeight: 700 }}>
                {status.label.toLowerCase()}
              </span>. Chat dialihkan melalui akun <strong>Superkos</strong> sampai
              koneksi properti aktif kembali.
            </Typography>
          </Box>
          
          {status.code === 'disconnected' && (
            <Box mt={2}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleOpenScan}
                startIcon={<CheckCircle2 size={16} />}
              >
                Hubungkan WhatsApp
              </Button>
            </Box>
          )}
        </Card>
      )} */}

          {/* Connection Status Card */}
          {/* {selectedProperty && status.code !== 'connected' && !usingFallback && (
        <Card className={classes.card} style={{ marginTop: 16 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <status.Icon size={20} style={{ color: status.color }} />
              <Typography variant="body2">
                Status: <strong style={{ color: status.color }}>{status.label}</strong>
              </Typography>
            </Box>
            
            {status.code === 'disconnected' && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleOpenScan}
              >
                Scan QR Code
              </Button>
            )}
          </Box>
        </Card>
      )} */}

          {/* WhatsApp Scan Dialog */}
          <Dialog open={scanOpen} onClose={handleCloseScan} maxWidth="md" fullWidth disableEscapeKeyDown>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Scan WhatsApp QR Code</Typography>
                <IconButton aria-label="close" onClick={handleCloseScan} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Properti: {selectedProperty?.name} • {selectedProperty?.phone}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <WhatsAppScan propertyId={selectedProperty?._id} phoneNumber={selectedProperty?.phone} status={statusSession} qrCode={qrCode} onSuccess={handleScanSuccess}/>
            </DialogContent>
          </Dialog>
        </Page>
      </FeatureGuard>
    </PermissionGate>);
});
const __Next_Translate__Page__19fef8642be__ = withAuthentication(WhatsAppIndex);

    export default __appWithI18n(__Next_Translate__Page__19fef8642be__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
    });
  